import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Autocomplete,
  FormControlLabel,
  Switch,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toTitleCaseArea } from "../../../../constants";

const ImportBatchModal = ({
  open,
  onClose,
  onImport,
  importing,
  areas = [],
  defaultAutoApprove = false,
}) => {
  const [batchName, setBatchName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  // Can be either an Area object ({id,name,...}) or a string when user free-types.
  const [defaultArea, setDefaultArea] = useState(null);
  const [autoApprove, setAutoApprove] = useState(!!defaultAutoApprove);
  const [touched, setTouched] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Default the Auto-Approve toggle to the org's "Import Batch Auto-Approve"
  // setting whenever the modal is opened (or the org setting loads).
  useEffect(() => {
    if (open) {
      setAutoApprove(!!defaultAutoApprove);
    }
  }, [open, defaultAutoApprove]);

  const normalizeAreaName = (value) => {
    try {
      return String(value || "")
        .trim()
        .replace(/\s+/g, " ");
    } catch {
      return "";
    }
  };

  const reservedAreaNames = new Set(["all", "nearby"]);

  const isReservedArea = (name) => {
    const normalized = normalizeAreaName(name);
    if (!normalized) return false;
    return reservedAreaNames.has(normalized.toLowerCase());
  };

  const selectableAreas = (areas || []).filter((a) => !isReservedArea(a?.name));

  const getDefaultAreaName = () => {
    if (!defaultArea) return "";
    if (typeof defaultArea === "string") return normalizeAreaName(defaultArea);
    return normalizeAreaName(defaultArea?.name);
  };

  const defaultAreaNameNormalized = getDefaultAreaName();
  const defaultAreaReservedError =
    !!defaultAreaNameNormalized &&
    reservedAreaNames.has(defaultAreaNameNormalized.toLowerCase());

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    if (selectedFile && selectedFile.type === "text/csv") {
      setFile(selectedFile);
      if (!batchName) {
        setBatchName(selectedFile.name.replace(/\.csv$/i, ""));
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    
    const droppedFile = event.dataTransfer.files?.[0] || null;
    if (droppedFile && droppedFile.type === "text/csv") {
      setFile(droppedFile);
      if (!batchName) {
        setBatchName(droppedFile.name.replace(/\.csv$/i, ""));
      }
    }
  };

  const handleImportClick = () => {
    setTouched(true);
    if (!batchName.trim() || !file || defaultAreaReservedError) {
      return;
    }
    onImport({
      batchName: batchName.trim(),
      description: description.trim(),
      file,
      defaultAreaName: defaultAreaNameNormalized,
      autoApprove,
    });
  };

  const handleClose = () => {
    if (importing) return;
    setBatchName("");
    setDescription("");
    setFile(null);
    setDefaultArea(null);
    setAutoApprove(!!defaultAutoApprove);
    setTouched(false);
    onClose();
  };

  const batchNameError = touched && !batchName.trim();
  const fileError = touched && !file;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import QuickTales Batch</DialogTitle>
      <DialogContent sx={{ minHeight: 660 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              CSV File
            </Typography>
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: isDragging ? "2px dashed #00bcd4" : fileError ? "2px dashed #d32f2f" : "2px dashed #ccc",
                borderRadius: 2,
                padding: 3,
                textAlign: "center",
                backgroundColor: isDragging ? "rgba(0, 188, 212, 0.05)" : "#fafafa",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#00bcd4",
                  backgroundColor: "rgba(0, 188, 212, 0.02)",
                },
              }}
            >
              <input
                type="file"
                id="csv-file-input"
                hidden
                accept=".csv,text/csv"
                onChange={handleFileChange}
              />
              <label htmlFor="csv-file-input" style={{ cursor: "pointer", width: "100%" }}>
                {file ? (
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: "#00bcd4" }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", mt: 1, display: "block" }}>
                      Click to change file or drag and drop a new one
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      Drag and drop CSV file here
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                      or
                    </Typography>
                    <Button
                      variant="outlined"
                      component="span"
                      sx={{
                        textTransform: "none",
                        color: "#00bcd4",
                        borderColor: "#00bcd4",
                      }}
                    >
                      Choose CSV file
                    </Button>
                  </Box>
                )}
              </label>
            </Box>
            {fileError && (
              <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
                CSV file is required
              </Typography>
            )}
            <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
              The CSV must include a header row with: photo, audio, title, description, told by first, told by last, told by email, who can listen, tags.
            </Typography>
          </Box>

          <TextField
            label="Batch Name"
            value={batchName}
            onChange={(e) => setBatchName(e.target.value)}
            fullWidth
            required
            error={batchNameError}
            helperText={batchNameError ? "Batch name is required" : ""}
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          <Autocomplete
            value={typeof defaultArea === "string" ? null : defaultArea}
            inputValue={typeof defaultArea === "string" ? defaultArea : (defaultArea?.name || "")}
            freeSolo
            onChange={(event, newValue) => setDefaultArea(newValue)}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input" && event?.target) {
                const { selectionStart, value } = event.target;
                // Only apply title case when typing forward (cursor at end)
                if (selectionStart === value.length) {
                  setDefaultArea(toTitleCaseArea(newInputValue));
                  return;
                }
              }
              setDefaultArea(newInputValue);
            }}
            options={selectableAreas}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Default Area (optional)"
                error={touched && defaultAreaReservedError}
                helperText={
                  touched && defaultAreaReservedError
                    ? '"All" and "Nearby" are reserved Area names. Please choose a different name.'
                    : "Used when CSV row has no Area value. If the Area doesn't exist, we will create it."
                }
              />
            )}
          />

          {/* Auto-Approve toggle */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoApprove}
                  onChange={(e) => setAutoApprove(e.target.checked)}
                  color="primary"
                />
              }
              label="Auto-Approve"
              sx={{ mr: 0 }}
            />
            <Tooltip
              title={
                    <span>
                    QuickTales are imported with a status of Pending. Once a QuickTale has a Narrator, Title, Photo and Audio, it needs to be changed manually to Approved to go live.
                    <br /><br />
                    Turning Auto-Approve on will automatically approve each QuickTale and make it live when it has all four elements.
                  </span>
              }
              placement="right"
              arrow
            >
              <InfoOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", cursor: "help" }} />
            </Tooltip>
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 500, mt: '-16px', ml: 1, color: autoApprove ? "#00a7cd" : "#000" }}>
              {autoApprove
                  ? "On"
                  : "Off"}
            </Typography>
          {autoApprove && (
            <Typography variant="caption" sx={{ color: "warning.main", mt: -1 }}>
              ⚠ QuickTales that have a title, photo, audio, and narrator will go live automatically — no review step.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ mt: '-24px'}}>
        <Button
          onClick={handleClose}
          disabled={importing}
          sx={{color: '#00a7cd'}}
          >
          Cancel
        </Button>
        <Button
          onClick={handleImportClick}
          variant="contained"
          disabled={importing}
            sx={{
              backgroundColor: '#00a7cd',
              '&:hover': {
                backgroundColor: '#0088a8',  // darker shade on hover
              },
            }}
        >
          {importing ? "Importing..." : "Import"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportBatchModal;
